import { urlComSessao } from "./sessao";
import type { Row, DashboardData, AlertasData, DocumentoLista, DocumentoFull } from "./types";

const BASE = "/api";

/**
 * Usuário logado nesta sessão. É enviado no header das operações de usuários
 * para o servidor validar que apenas administradores criam/editam/excluem.
 */
let sessionUser = "";
export function setSessionUser(u: string) {
  sessionUser = u ?? "";
}
const authHeaders = () => ({ "x-sercamp-usuario": sessionUser });

export class ApiError extends Error {
  code: string;
  constructor(message: string, code = "ERROR") {
    super(message);
    this.code = code;
  }
}

async function handle(res: Response) {
  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    let code = "ERROR";
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
      if (body?.code) code = body.code;
    } catch {
      /* ignora */
    }
    throw new ApiError(msg, code);
  }
  return res.json();
}

export const api = {
  list: (resource: string): Promise<Row[]> =>
    fetch(`${BASE}/${resource}`, { headers: authHeaders() }).then(handle),

  create: (resource: string, data: Row): Promise<Row> =>
    fetch(`${BASE}/${resource}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),

  update: (resource: string, id: number, data: Row): Promise<Row> =>
    fetch(`${BASE}/${resource}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),

  remove: (resource: string, id: number): Promise<{ ok: boolean }> =>
    fetch(`${BASE}/${resource}/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    }).then(handle),

  dashboard: (): Promise<DashboardData> =>
    fetch(`${BASE}/dashboard`, { headers: authHeaders() }).then(handle),

  /**
   * Retorna { usuario, nome, cargo, isAdmin, podeExcluir } do usuário informado.
   * `podeExcluir` diz se as telas devem mostrar os botões de excluir (só admin).
   */
  me: (
    usuario: string,
  ): Promise<{
    usuario: string;
    nome: string | null;
    cargo: string | null;
    isAdmin: boolean;
    podeExcluir: boolean;
  }> => fetch(`${BASE}/me?u=${encodeURIComponent(usuario)}`).then(handle),

  /**
   * Portão de acesso — perguntado ao PRÓPRIO servidor, não ao Portal.
   *
   * O servidor conhece as duas portas de entrada (cadastro local e liberação no
   * Portal); o Portal só conhece a dele. Perguntando lá, quem entrava com o
   * login local via "Acesso negado" com a senha certa. Agora a tela e a guarda
   * das rotas usam exatamente a mesma decisão.
   *
   * `code: "PORTAL_INDISPONIVEL"` = não deu para confirmar (≠ negado).
   *
   * `somenteLeitura` / `abas`: acesso SOMENTE VISUALIZAÇÃO do cadastro do
   * Portal — abas liberadas (null = todas) e nada de gravar. Ver src/modo-leitura.ts.
   */
  acesso: (usuario: string): Promise<{
    allowed: boolean; adminSupremo: boolean; code: string | null; motivo: string | null;
    somenteLeitura?: boolean; abas?: string[] | null;
  }> => fetch(`${BASE}/acesso?u=${encodeURIComponent(usuario)}`, { headers: authHeaders() }).then(handle),

  /** Alertas de estoque (itens abaixo do mínimo/zerados) + notificação por e-mail. */
  alertas: {
    get: (): Promise<AlertasData> => fetch(`${BASE}/alertas`, { headers: authHeaders() }).then(handle),
    notificar: (): Promise<{ ok: boolean; total: number; enviadosPara?: string[]; mensagem?: string }> =>
      fetch(`${BASE}/alertas/notificar`, { method: "POST", headers: authHeaders() }).then(handle),
  },

  /** Documentos — termo de retirada/devolução (movimenta o estoque). */
  /**
   * RECEBIMENTO DE MATERIAIS — entrada com conferência. O estoque só é
   * movimentado no `conferir`. Ver server/recebimentos.ts.
   */
  recebimentos: {
    list: (): Promise<Row[]> =>
      fetch(`${BASE}/recebimentos`, { headers: authHeaders() }).then(handle),

    get: (id: number): Promise<Row> =>
      fetch(`${BASE}/recebimentos/${id}`, { headers: authHeaders() }).then(handle),

    create: (data: Row): Promise<{ id: number; status: string }> =>
      fetch(`${BASE}/recebimentos`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    conferir: (
      id: number,
      data: Row,
    ): Promise<{ ok: boolean; status: string; movimentados: number; mensagem: string }> =>
      fetch(`${BASE}/recebimentos/${id}/conferir`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    remove: (id: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/recebimentos/${id}`, { method: "DELETE", headers: authHeaders() }).then(handle),
  },

  documentos: {
    list: (): Promise<DocumentoLista[]> =>
      fetch(`${BASE}/documentos`, { headers: authHeaders() }).then(handle),

    get: (id: number): Promise<DocumentoFull> =>
      fetch(`${BASE}/documentos/${id}`, { headers: authHeaders() }).then(handle),

    create: (data: Row): Promise<{ id: number; status: string }> =>
      fetch(`${BASE}/documentos`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    /**
     * Endereço do TERMO no formulário oficial (Word), montado na hora pelo
     * servidor com o que está gravado — ver server/termo-docx.ts.
     */
    termoUrl: (id: number): string => urlComSessao(`${BASE}/documentos/${id}/termo.docx`),

    devolver: (id: number, data: Row): Promise<{ ok: boolean; status: string }> =>
      fetch(`${BASE}/documentos/${id}/devolucao`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    remove: (id: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/documentos/${id}`, { method: "DELETE", headers: authHeaders() }).then(handle),
  },
};
